import { Logo } from '@firmable/icons';
import { Routes } from '@web/constants/routes';
import { getConversionStatus, hasSubscription } from '@web/guards/Subscription';
import { useSubscriptions } from '@web/query/App/Stripe/useSubscriptions';
import { useMetadata } from '@web/query/Customer/useMetadata';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { PropsWithChildren } from 'react';

interface Props extends PropsWithChildren {}

export function SubscriptionGuard({ children }: Props) {
  const { data: subscriptionData, isLoading: subscriptionLoading } = useSubscriptions();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  // The conversion-Checkout return. Within seconds of landing, the new subscription goes active and the
  // old trial is retired, both of which make getConversionStatus() null; the processing screen owns that exit.
  const isConversionReturn = pathname === Routes.planSelectionPath && !!searchParams.get('conversion-session');

  const hasValidSubscription = hasSubscription(subscriptionData);
  const conversionStatus = getConversionStatus(subscriptionData);
  const { data: metadataData, isLoading: metadataLoading } = useMetadata({ enabled: hasValidSubscription });
  const hasNotAcceptedTerms = metadataData?.metadata?.termsAcceptedAt === null;

  const loader = React.useMemo(() => {
    return (
      <div className="flex justify-center items-center w-screen h-screen bg-surface-light">
        <Logo.FirmableShort width={48} height={48} className={'animate-ping'} />
      </div>
    );
  }, []);

  /**
   * The billing lock is owned by apps/web/proxy.ts, which decides from the session claim.
   * Bail out before any redirect below, or the two disagree and bounce the user between
   * routes: the proxy sends a paused org here, and a redirect from here sends them to a
   * route the proxy immediately sends back. Skipping the loader also lets the block render
   * straight away rather than behind a spinner.
   */
  if (pathname === Routes.billingPausedPath) {
    return children;
  }

  if (subscriptionLoading || metadataLoading) {
    return loader;
  }

  if (hasNotAcceptedTerms && hasValidSubscription && pathname !== Routes.acceptTermsPath) {
    router.replace(Routes.acceptTermsPath);
    return loader;
  }

  /**
   * The two routes an organisation with no subscription is allowed to sit on: plan selection,
   * and the trial-or-subscribe step that precedes it when the no-card trial toggle is on.
   */
  const isSubscriptionChoicePath = pathname === Routes.planSelectionPath || pathname === Routes.startTrialPath;

  if (!hasValidSubscription && !isSubscriptionChoicePath) {
    router.replace(Routes.planSelectionPath);
    return loader;
  }

  /**
   * An organisation that already has a subscription is always sent off the trial step, and off plan
   * selection unless it is there to convert: trialing with the picker as its only way to a card, or
   * paused after the trial ended. The proxy admits a paused organisation to that one route for the
   * same reason; the two must agree or the user bounces between them.
   */
  if (hasValidSubscription && pathname === Routes.startTrialPath) {
    router.replace(Routes.dashboardRoot);
    return loader;
  }

  if (
    hasValidSubscription &&
    pathname === Routes.planSelectionPath &&
    conversionStatus === null &&
    !isConversionReturn
  ) {
    router.replace(Routes.dashboardRoot);
    return loader;
  }

  return children;
}
